/**
 * B.7.e Step 3 — sanitize gate. Calls the OpenRouter-backed Convex
 * action `sanitizeAction.sanitizeText` for each uploaded file, renders
 * the diff, requires operator confirmation per doc before continuing.
 */

import { useState } from 'react';
import { useAction } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import type { WizardState } from './WizardShell';

export interface SanitizeOutcome {
  fileName: string;
  status: 'pending' | 'running' | 'ok' | 'failed';
  sanitizedBody?: string;
  diffHtml?: string;
  replacementCount?: number;
  errorMessage?: string;
  confirmed: boolean;
}

interface Step3Props {
  state: WizardState;
  onBack: () => void;
  onContinue: (outcomes: Record<string, SanitizeOutcome>) => void;
}

export function Step3Sanitize({ state, onBack, onContinue }: Step3Props) {
  const sanitizeText = useAction(api.sanitizeAction.sanitizeText);
  const [outcomes, setOutcomes] = useState<Record<string, SanitizeOutcome>>(
    () => {
      const initial: Record<string, SanitizeOutcome> = {};
      for (const f of state.uploadedFiles) {
        initial[f.name] = state.sanitizeOutcomes[f.name] ?? {
          fileName: f.name,
          status: 'pending',
          confirmed: false,
        };
      }
      return initial;
    },
  );
  const [batchRunning, setBatchRunning] = useState(false);

  async function runSanitize(fileName: string) {
    const file = state.uploadedFiles.find((f) => f.name === fileName);
    if (file === undefined) return;
    setOutcomes((cur) => ({
      ...cur,
      [fileName]: { ...cur[fileName]!, status: 'running' },
    }));
    try {
      const result = await sanitizeText({
        body: file.body,
        documentType: file.documentType,
        targetTemplateSlug: state.targetClientSlug,
      });
      if (result.ok) {
        setOutcomes((cur) => ({
          ...cur,
          [fileName]: {
            fileName,
            status: 'ok',
            sanitizedBody: result.sanitizedBody,
            diffHtml: result.diff.diffHtml,
            replacementCount: result.diff.replacements.length,
            confirmed: false,
          },
        }));
      } else {
        setOutcomes((cur) => ({
          ...cur,
          [fileName]: {
            fileName,
            status: 'failed',
            errorMessage: result.reason,
            confirmed: false,
          },
        }));
      }
    } catch (err) {
      setOutcomes((cur) => ({
        ...cur,
        [fileName]: {
          fileName,
          status: 'failed',
          errorMessage: err instanceof Error ? err.message : String(err),
          confirmed: false,
        },
      }));
    }
  }

  async function runAll() {
    setBatchRunning(true);
    for (const f of state.uploadedFiles) {
      if (outcomes[f.name]?.status === 'ok') continue;
      await runSanitize(f.name);
    }
    setBatchRunning(false);
  }

  function toggleConfirm(name: string) {
    setOutcomes((cur) => ({
      ...cur,
      [name]: { ...cur[name]!, confirmed: !cur[name]!.confirmed },
    }));
  }

  const allConfirmed =
    state.uploadedFiles.length > 0 &&
    state.uploadedFiles.every(
      (f) =>
        outcomes[f.name]?.status === 'ok' &&
        outcomes[f.name]?.confirmed === true,
    );

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-2">Review sanitisation</h2>
        <p className="text-sm text-gray-800 mb-3">
          Each file runs through the three-layer sanitiser (deterministic
          regex pre-pass, Claude contextual replacement, diff). Confirm
          each doc to acknowledge no client-identifying detail remains.
        </p>
        <button
          type="button"
          data-testid={testIds.portalWizard.sanitizeProgress}
          onClick={() => void runAll()}
          disabled={batchRunning}
          className="border border-black bg-white px-4 py-2 text-sm hover:bg-gray-100 disabled:opacity-50"
        >
          {batchRunning ? 'Sanitising…' : 'Run sanitiser on all files'}
        </button>
      </div>

      <ul className="space-y-4">
        {state.uploadedFiles.map((f) => {
          const o = outcomes[f.name]!;
          return (
            <li key={f.name} className="border border-black/30 bg-white">
              <header className="flex items-center justify-between px-3 py-2 border-b border-black/20 bg-gray-100">
                <span className="font-semibold text-sm">{f.name}</span>
                <span className="text-xs text-gray-800">
                  {o.status === 'pending' && 'Not yet sanitised'}
                  {o.status === 'running' && 'Sanitising…'}
                  {o.status === 'ok' &&
                    `Sanitised · ${o.replacementCount ?? 0} replacements`}
                  {o.status === 'failed' && 'Failed'}
                </span>
              </header>
              {o.status === 'ok' && o.diffHtml !== undefined && (
                <pre
                  data-testid={testIds.portalWizard.sanitizeDiff(f.name)}
                  className="text-xs whitespace-pre-wrap px-3 py-2 max-h-64 overflow-auto"
                  dangerouslySetInnerHTML={{ __html: o.diffHtml }}
                />
              )}
              {o.status === 'failed' && (
                <p className="text-xs text-red-700 px-3 py-2">
                  {o.errorMessage ?? 'Unknown error'}
                </p>
              )}
              <footer className="px-3 py-2 flex items-center gap-2 border-t border-black/20 bg-gray-50">
                {o.status !== 'ok' && (
                  <button
                    type="button"
                    onClick={() => void runSanitize(f.name)}
                    disabled={o.status === 'running'}
                    className="border border-black bg-white px-3 py-1 text-xs hover:bg-gray-100 disabled:opacity-50"
                  >
                    {o.status === 'running' ? 'Running…' : 'Run sanitiser'}
                  </button>
                )}
                {o.status === 'ok' && (
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={o.confirmed}
                      onChange={() => toggleConfirm(f.name)}
                      data-testid={testIds.portalWizard.sanitizeConfirm(f.name)}
                    />
                    I confirm no client-identifying detail remains.
                  </label>
                )}
              </footer>
            </li>
          );
        })}
      </ul>

      <div className="flex gap-2">
        <button
          type="button"
          data-testid={testIds.portalWizard.step3Back}
          onClick={onBack}
          className="border border-black bg-white px-4 py-2 text-sm hover:bg-gray-100"
        >
          Back
        </button>
        <button
          type="button"
          data-testid={testIds.portalWizard.step3Next}
          disabled={!allConfirmed}
          onClick={() => onContinue(outcomes)}
          className="border border-black bg-white px-4 py-2 text-sm disabled:opacity-50 hover:bg-gray-100"
        >
          Next: Provision portal
        </button>
      </div>
    </section>
  );
}
